'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import styles from './BestBuyHomeCta.module.css';

const loadHomePicker = () =>
  import('./HomePicker').then((mod) => mod.HomePicker);

const HomePicker = dynamic(loadHomePicker, { ssr: false });

type Props = {
  /** The hero owns the button's look; it passes its pill classes in. */
  className?: string;
};

/**
 * Homepage Best Buy pickup entry: the US hero's secondary CTA. Opens the store
 * picker; the picker code is fetched on intent (pointer, focus, touch).
 */
export const BestBuyHomeCta = ({ className }: Props) => {
  const t = useTranslations('homepage');
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const preload = useCallback(() => {
    void loadHomePicker();
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Focus goes back to the button once the picker has unmounted, so it lands
  // there even where a click does not focus a button (Safari).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open && wasOpenRef.current) buttonRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`${className ?? ''} ${styles.cta}`}
        aria-haspopup="dialog"
        data-buttontype="bb_pickup_home_entry"
        data-testid="bb-home-cta"
        onClick={() => setOpen(true)}
        onPointerEnter={preload}
        onFocus={preload}
        onTouchStart={preload}
      >
        <span className={styles.label}>{t('bestBuyPickup.cta.label')}</span>
      </button>
      {open ? <HomePicker onClose={close} /> : null}
    </>
  );
};
